import { useEffect, useState } from 'react'
import { Button, Dialog, useToast } from '@platform/ui-kit'
import {
  dismissPushPrompt,
  dismissedPushPrompt,
  enablePush,
  forgetPushPromptDismissal,
  isPushSupported,
  pushState,
  serverPushKey,
} from '@/lib/push'

/* ------------------------------------------------------------------
   دروازهٔ فعال‌سازی اعلان — پس از ورود

   ==================================================================
   🔴 اعلانِ داخل برنامه فقط وقتی دیده می‌شود که کاربر برنامه را باز
   کرده باشد — و مدیری که منتظر تأییدِ اوییم، معمولاً بازش نکرده.

   ⚠️ صفحهٔ اعلان‌ها یک دکمهٔ فعال‌سازی داشت، ولی کسی که خبر ندارد
   اعلانی هست، سراغ صفحهٔ اعلان‌ها هم نمی‌رود. دکمه‌ای که پیدا نشود،
   با نبودنش فرقی ندارد.

   ==================================================================
   🔴 ۱۴۰۵/۰۸/۰۵ صاحبِ پروژه: «این الرت که اولِ ورود برای نوتیف میاد رو
   بردار؛ کاربر وقتی خودش یک بار دسترسیِ نوتیف رو بده باید اوکی باشه.»
   پس مودال فقط یک پرسش دارد — «اجازهٔ اعلان می‌دهی؟» — و فقط تا وقتی
   مرورگر هنوز جوابی نگرفته (`Notification.permission === 'default'`):
     · اجازه داده شده ⟶ دیگر هرگز مودالی نیست؛ اشتراکِ Push بی‌صدا
       کامل می‌شود و اگر نشد، دفعهٔ بعد دوباره بی‌صدا امتحان می‌شود؛
     · رد شده ⟶ تصمیمِ کاربر است، دیگر نمی‌پرسیم (صفحهٔ «اعلان‌ها»
       راهِ برگشت را دارد)؛
     · مرورگرِ بی‌پشتیبانی یا سرورِ بی‌کلید ⟶ کاری از دستِ کاربر
       برنمی‌آید، پس چیزی هم نشان داده نمی‌شود (پیش‌تر هر ورود یک
       راهنمای «سرور پیکربندی نشده است» روی صفحه می‌نشست).
   با یک کلیک مودال بسته می‌شود، هر جوابی که مرورگر بدهد؛ پنجرهٔ اجازهٔ
   بی‌پاسخ تا ورودِ بعدی دوباره پرسیده نمی‌شود (`dismissPushPrompt`).
   ==================================================================
   ------------------------------------------------------------------ */

/*
  چیزهایی که با فعال‌بودن اعلان به کاربر می‌رسد.

  ⚠️ عمداً همان سه منبعی است که واقعاً Push می‌فرستند — مرخصی،
  جانشینی و میز خدمت.

  🔴 وعدهٔ چیزی که نمی‌آید، بدترین نوع اعلان است: کاربر منتظر می‌ماند،
  خبری نمی‌شود، و دفعهٔ بعد هیچ اعلانی را جدی نمی‌گیرد.
*/
const BENEFITS = [
  { icon: '/icons/3d/page-leave-approvals.png', label: 'درخواست‌هایی که منتظر تأیید شما هستند' },
  { icon: '/icons/3d/leave.png', label: 'پاسخ جانشین به درخواست مرخصی شما' },
  { icon: '/icons/3d/service-desk.png', label: 'تیکت‌های تازه‌ای که به شما ارجاع می‌شود' },
] as const

export function PushEnrollmentGate() {
  const toast = useToast()

  const [ask, setAsk] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false

    /*
      ⚠️ وضعیت از خودِ مرورگر پرسیده می‌شود، نه از سرور.

      🔴 سرور فقط می‌داند «اشتراکی ثبت شده»؛ اینکه کاربر بعداً اجازه را
      از تنظیمات پس گرفته یا داده‌های سایت را پاک کرده، نمی‌داند.
    */
    const check = async () => {
      if (!isPushSupported() || (await pushState()) !== 'unsubscribed') return

      if (Notification.permission === 'granted') {
        /* 🔴 کاربر قبلاً اجازه داده: بی مودال، فقط اشتراک کامل می‌شود. */
        void enablePush().catch(() => undefined)

        return
      }

      if (Notification.permission !== 'default' || dismissedPushPrompt() !== null) return

      /* ⚠️ سرورِ بی‌کلید: پرسیدنِ اجازه‌ای که به هیچ اعلانی نمی‌رسد بی‌معناست. */
      try {
        const key = await serverPushKey()

        if (!key.enabled || !key.public_key) return
      } catch {
        return
      }

      if (!cancelled) setAsk(true)
    }

    void check()

    return () => {
      cancelled = true
    }
  }, [])

  const enable = async () => {
    setBusy(true)

    try {
      const next = await enablePush()

      if (next === 'subscribed') {
        forgetPushPromptDismissal()
        toast.success('اعلان روی این دستگاه فعال شد.')
      } else if (next === 'denied') {
        toast.toast({
          tone: 'info',
          title: 'اجازهٔ اعلان داده نشد.',
          description: 'هر وقت خواستید از صفحهٔ «اعلان‌ها» یا نشانِ قفلِ کنارِ نشانیِ سایت روشنش کنید.',
        })
      } else {
        /* پنجرهٔ اجازه بی‌پاسخ بسته شد (یا سرور و مرورگر همین حالا ناممکن شدند) — تا ورودِ بعدی نه */
        dismissPushPrompt(next)
      }
    } catch {
      /*
        ⚠️ اجازه گرفته شده ولی اشتراکِ Push نشد (سرویسِ Push ِ مرورگر در دسترس
        نبود). دوباره مودال نمی‌آید: بارِ بعد همان مسیرِ بی‌صدای «اجازه داده
        شده» امتحانش می‌کند.
      */
      if (Notification.permission !== 'granted') dismissPushPrompt('failed')
    } finally {
      setBusy(false)
      setAsk(false)
    }
  }

  if (!ask) return null

  return (
    <Dialog
      open
      /*
        ⚠️ `Dialog` همیشه با Escape بسته می‌شود و این تضمینِ دسترس‌پذیری
        است؛ بستن هم یعنی «فعلاً نه» — تا ورودِ بعدی دوباره پرسیده نمی‌شود.
      */
      onClose={() => {
        dismissPushPrompt('dismissed')
        setAsk(false)
      }}
      closeOnOverlayClick={false}
      size="sm"
      title="فعال‌سازی اعلان روی این دستگاه"
      description="تأییدها و کارهایی که به شما ارجاع می‌شود، باید به‌موقع به دستتان برسد — نه وقتی که اتفاقی سامانه را باز کردید."
      footer={
        /*
          ⚠️ در موبایل دکمه تمام‌عرض است.

          🔴 دکمهٔ اصلیِ این مودال در عرض کم نباید گوشه‌ای کوچک باشد که
          کاربر دنبالش بگردد.
        */
        <Button
          variant="primary"
          loading={busy}
          onClick={() => void enable()}
          fullWidth
        >
          فعال‌سازی اعلان
        </Button>
      }
    >
      <div className="space-y-[var(--pf-space-5)]">
        {/*
          ⚠️ تصویر روی یک هالهٔ برند می‌نشیند، نه روی زمینهٔ خالی.

          🔴 آیکون‌های سه‌بعدی پس‌زمینهٔ شفاف دارند و رها روی سطحِ
          مودال «چسبانده‌شده» به نظر می‌رسند. حلقهٔ رنگی جایشان را
          تعریف می‌کند و نگاه را اول به همان می‌برد.
        */}
        <div className="flex justify-center">
          <div className="relative flex size-28 items-center justify-center">
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full bg-[var(--pf-brand-50)]"
            />

            {/*
              ⚠️ `alt` خالی و `aria-hidden`: عنوانِ مودال همان را
              می‌گوید و تکرارش با صفحه‌خوان چیزی اضافه نمی‌کند.
            */}
            <img
              src="/icons/3d/notifications.png"
              alt=""
              aria-hidden="true"
              width={96}
              height={96}
              decoding="async"
              className="relative size-24 object-contain"
            />
          </div>
        </div>

        {/*
          ⚠️ هر سطر یک آیکونِ واقعیِ همان ماژول دارد.

          🔴 فهرستِ نقطه‌ای سه خطِ هم‌شکل بود که چشم رویشان سُر
          می‌خورد. آیکونِ ماژول همان چیزی است که کاربر در سایدبار هم
          می‌بیند، پس «این‌ها همان کارهای من‌اند» را بدون توضیح
          می‌رساند.
        */}
        <ul className="space-y-[var(--pf-space-2)]">
          {BENEFITS.map((benefit) => (
            <li
              key={benefit.label}
              className="flex items-center gap-[var(--pf-space-3)] rounded-[var(--pf-radius-md)] bg-[var(--pf-surface-muted)] p-[var(--pf-space-3)]"
            >
              <img
                src={benefit.icon}
                alt=""
                aria-hidden="true"
                width={32}
                height={32}
                loading="lazy"
                decoding="async"
                className="size-8 shrink-0 object-contain"
              />

              <span className="text-[length:var(--pf-text-sm)] leading-relaxed text-[var(--pf-text)]">
                {benefit.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  )
}
